import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import DashboardLayout from "../../components/DashboardLayout";
import WalletOverview from "./WalletOverview";
import Tabs from "../../components/Common/Tabs";
import WalletSummaryCard from "../../components/Wallet/WalletSummaryCard";
import WithdrawFunds from "./WithdrawFunds";
import TransactionHistory from "./TransactionHistory";
import WalletSetting from "./WalletSetting";
import WelcomeCard from "../../components/Wallet/WelcomeCard";
import { useNavigate } from "react-router";
import { useGetWalletQuery } from "../../redux/services/Wallet";
import FleetrifyBlockSkeleton from "../../components/Loading/BlockSkeleton";
import { usePermission } from "../../hooks/usePermission";
import { selectUser } from "../../redux/slices/authSlice";
import { toast } from "sonner";
const Wallet = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<string | number>("overview");

  const user = useSelector(selectUser);
  const companyId = user?.company?.id || "";


 const {
   data: walletData,
   isLoading,
   isError,
 } = useGetWalletQuery(companyId, {
   refetchOnMountOrArgChange: true,
   skip: !companyId,
 });
const { hasPermission } = usePermission();

  const wallet = walletData?.data?.data?.wallet;



  const tabList = useMemo(
    () => [
      { label: "Wallet Overview", value: "overview" },
      // { label: "Fund Wallet", value: "fund" },
      { label: "Withdraw Funds", value: "withdraw" },
      // { label: "Send Money", value: "send" },
      { label: "Transaction History", value: "transactions" },
      { label: "Wallet Settings", value: "settings" },
    ],
    []
  );

  
  const tabContentMap = useMemo<
    Record<string, { title: string; description: string }>
  >(
    () => ({
      overview: {
        title: "Wallet",
        description:
          "Get a clear, actionable view of your wallet balance, funds, and key metrics all in one place.",
      },
      // fund: {
      //   title: "Fund Wallet",
      //   description:
      //     "Add funds to your wallet securely using your preferred payment method.",
      // },
      withdraw: {
        title: "Withdraw Funds",
        description:
          "Easily transfer money from your wallet to your linked bank account.",
      },
      // send: {
      //   title: "Send Money",
      //   description:
      //     "Send money instantly to other users or accounts within your organization.",
      // },
      transactions: {
        title: "Transaction History",
        description:
          "View and track all wallet transactions, including deposits, withdrawals, and transfers.",
      },
      settings: {
        title: "Wallet Settings",
        description:
          "Manage your wallet preferences, security options, and linked accounts.",
      },
    }),
    []
  );

  const { title, description } = useMemo(() => {
    return tabContentMap[view] || tabContentMap.overview;
  }, [view, tabContentMap]);
const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      scrollRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, [view]);

  if (isLoading) {
    return (
      <DashboardLayout
        title="Wallet"
        description="Loading wallet information..."
        containerClass="space-y-10"
      >
        <FleetrifyBlockSkeleton lines={5} className="mt-4" />
      </DashboardLayout>
    );
  }

  if (isError) {
    return (
      <DashboardLayout
        title="Wallet"
        description="Error fetching wallet information"
        containerClass="space-y-10"
      >
        <div className="text-center bg-red-50 border border-red-200 p-8 rounded-xl mt-10 mx-auto md:w-1/2">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 p-4 rounded-full">
              <svg
                className="w-8 h-8 text-red-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L4.34 17c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>

          <h2 className="text-lg font-semibold text-red-600">
            Unable to Load Wallet
          </h2>

          <p className="text-gray-600 mt-2">
            Something went wrong while loading your wallet information. This
            could be caused by a server issue, incomplete data, or a temporary
            error. Please try again.
          </p>

          <button
            onClick={() => window.location.reload()}
            className="mt-5 px-5 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
          >
            Retry
          </button>
        </div>
      </DashboardLayout>
    );
  }



  if (!wallet?.wallet_profile_id) {
    return (
      <DashboardLayout
        title="Wallet"
        description="Set up your Fleetrify Wallet"
        containerClass="space-y-10"
      >
        <WelcomeCard
          title="Welcome to Fleetrify Wallet"
          description="Easily send and receive money, top up your account, pay drivers, and more — all from one secure place."
          buttonText="Get Started"
          imageUrl="/images/wallet-welcome.svg"
          additionalText="Let’s get your wallet set up in just a few steps."
          clickFunc={() => navigate("/wallet/wallet-setup")}
          isEmoji={true}
        />
      </DashboardLayout>
    );
  }
  return (
    <DashboardLayout
      title={title}
      description={description}
      containerClass="space-y-10"
    >
      <div ref={scrollRef}>
        <section className="bg-white p-4 rounded-lg mt-10 border border-gray-300">
          <Tabs tabs={tabList} activeTab={view} onChange={setView} />
          <div className="mt-8">
            {view === "overview"  &&(
              <div>
                <WalletSummaryCard
                  balance={parseFloat(wallet.balance)}
                  // lastTopUpAmount={50000}
                  // lastTopUpDate="Jun 27, 2025"
                  // walletId={wallet.account_number}
                  // walletName={wallet.account_name}
                  // lastSynced="5 mins ago"
                  trendPercentage={wallet.balance_trend?.trend_percentage}
                  trend={wallet.balance_trend?.trend}
                  verified={wallet.status === "active"}
                />
                <WalletOverview /> 
              </div>
            )}
            {view === "fund" && <div>Fund Wallet Content</div>}
            {view === "withdraw" && (
              <div>
                <div>
                  <WalletSummaryCard
                    balance={parseFloat(wallet.balance)}
                    // walletId={wallet.account_number}
                    // walletName={wallet.account_name}
                    trendPercentage={wallet.balance_trend?.trend_percentage}
                    trend={wallet.balance_trend?.trend}
                    verified={wallet.status === "active"}
                  />
                  <div className="mt-12">
                    {!hasPermission("access to withdraw") ? (
                      <div className="text-center bg-yellow-50 border border-yellow-200 p-8 rounded-xl mt-10 mx-auto md:w-1/2">
                        <div className="flex justify-center mb-4">
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 22s8-4 8-10V5l-8 3-8-3v7c0 6 8 10 8 10z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Withdrawal Not Allowed
                        </h3>
                        <p className="text-gray-600">
                          You do not have permission to withdraw funds from this wallet.
                        </p>
                      </div>
                    ) : (
                      <WithdrawFunds
                        onCompleted={() => {
                            setView("overview");
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            {view === "send" && <div>Send Money Content</div>}
            {view === "transactions" && (
              <div className="mt-12">
                    {!hasPermission("view transaction histories") ? (
                      <div className="text-center bg-yellow-50 border border-yellow-200 p-8 rounded-xl mt-10 mx-auto md:w-1/2">
                        <div className="flex justify-center mb-4">
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 22s8-4 8-10V5l-8 3-8-3v7c0 6 8 10 8 10z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Access to view transactions denied
                        </h3>
                        <p className="text-gray-600">
                          You do not have permission to view transaction histories.
                        </p>
                      </div>
                    ) : (  
                        <TransactionHistory />
        
                    )}
                  </div>
            )}
            {view === "settings" && (
              <div className="mt-12">
              {!hasPermission("view wallet settings") ? (
                      <div className="text-center bg-yellow-50 border border-yellow-200 p-8 rounded-xl mt-10 mx-auto md:w-1/2">
                        <div className="flex justify-center mb-4">
                          <svg
                            width="48"
                            height="48"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 22s8-4 8-10V5l-8 3-8-3v7c0 6 8 10 8 10z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Access to view wallet settings denied
                        </h3>
                        <p className="text-gray-600">
                          You do not have permission to view wallet settings.
                        </p>
                      </div>
                    ) : (  
                        <WalletSetting />
        
                    )}
                    </div>
            )}
          </div>
        </section>
      </div>
    </DashboardLayout>
  );
};

export default Wallet;
